/**
 * One game, from its start to the next: where it connects, the connection's
 * state and what the server has said about me. Games starts a session through
 * startSession, which builds every field afresh, so a new game cannot inherit
 * a value from the last one; the handlers that learn the rest from the server
 * (Network's login packet, UI's score and level, Player's spawn, Players'
 * reteam, Games' spectate) write the fields they learn.
 */
import type { GAME_TYPES } from '@airmash/protocol';
import type { PlaneType } from './Constants';

/**
 * The session's states, numbered as legacy's game.state was. ab-protocol carried this table, though it never travels
 * on the wire, so it lives here with the state it names.
 */
export const GAME_STATE_CODES = {
  LOGIN: 1,
  CONNECTING: 2,
  PLAYING: 3,
} as const;

export type GAME_STATE_CODES = (typeof GAME_STATE_CODES)[keyof typeof GAME_STATE_CODES];

/** Where a game is played: a region's room on a game server, or a dev build's `#connect` URL. */
export type GameTarget =
  | {
      readonly kind: 'server';
      readonly regionId: string;
      readonly roomId: string;
      readonly host: string;
      readonly path: string;
    }
  | { readonly kind: 'custom'; readonly url: string };

/**
 * The server's settings from the LOGIN packet's configuration string. ab-server
 * sends sf and botsNamePrefix always, and afk when its AFK kick is on; ours add
 * ext when the socket URL asked for an extension level.
 */
export interface ServerConfig {
  /** Minutes of inactivity before the server drops a player; the AFK notice names it. */
  readonly afk?: number;
  /** The prefix the server gives bot names, which NameCleanup strips. */
  readonly botsNamePrefix?: string;
  /**
   * The connection's protocol extension level; without it, the stock protocol. Nothing reads it
   * yet: the features a level enables will be named here, so game code never compares levels.
   */
  readonly ext?: number;
  /** The server's scale cap: the view area a client may declare is sf² / 16. UI.serverZoomCap holds the zoom to it. */
  readonly sf?: number;
}

export interface Session {
  /** Where this session connects, or null in the lobby before the first game. */
  readonly target: GameTarget | null;
  /** The target's id for telemetry and error reports: `region-room`, or `custom-` and the URL; null in the lobby. */
  readonly serverId: string | null;
  /** LOGIN is the lobby, CONNECTING a socket opening or down, PLAYING logged in. */
  state: GAME_STATE_CODES;
  /** From the LOGIN packet; null before it, and cleared while the connection is down, as legacy did. */
  serverConfig: ServerConfig | null;
  /** From the LOGIN packet; null before it. */
  gameType: GAME_TYPES | null;
  /** From the LOGIN packet, stored raw: the scoreboard header is a text node. */
  roomName: string;
  /** My player id from the LOGIN packet; null before it. */
  myID: number | null;
  /** My plane from my spawn; null until I first spawn. */
  myType: PlaneType | null;
  /** My team from the LOGIN packet and each reteam; null before login. */
  myTeam: number | null;
  /** The name this session logged in with; a switch logs in with it again. */
  myName: string;
  /** My score from the score updates, 0 at the start. */
  myScore: number;
  /** My level from the level packets, 0 at the start. */
  myLevel: number;
  /** The player being spectated, or null while playing. */
  spectatingID: number | null;
}

/** The lobby before the first game, then each game as it starts; importers read the current one through this live binding. */
export let session: Session = newSession(null, '');

/** Starts a session for the target: state CONNECTING, the server id from the target, and everything else at its start value. */
export function startSession(target: GameTarget, name: string): void {
  session = newSession(target, name);
}

/** The current session's target, which exists once a game has started. */
export function requireTarget(): GameTarget {
  if (session.target === null) {
    throw new Error('No game has started');
  }
  return session.target;
}

/**
 * Reads the LOGIN packet's configuration string, keeping each known field only
 * when it has its type, so a server sending something else cannot put a
 * number where a name prefix goes. Anything unreadable is an empty config.
 */
export function parseServerConfig(text: string): ServerConfig {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return {};
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return {};
  }
  const config: { afk?: number; botsNamePrefix?: string; ext?: number; sf?: number } = {};
  if ('afk' in parsed && typeof parsed.afk === 'number') {
    config.afk = parsed.afk;
  }
  if ('botsNamePrefix' in parsed && typeof parsed.botsNamePrefix === 'string') {
    config.botsNamePrefix = parsed.botsNamePrefix;
  }
  if ('ext' in parsed && typeof parsed.ext === 'number') {
    config.ext = parsed.ext;
  }
  if ('sf' in parsed && typeof parsed.sf === 'number') {
    config.sf = parsed.sf;
  }
  return config;
}

/** The id telemetry and error reports name a target by. */
function serverIdOf(target: GameTarget): string {
  return target.kind === 'custom' ? `custom-${target.url}` : `${target.regionId}-${target.roomId}`;
}

function newSession(target: GameTarget | null, name: string): Session {
  return {
    target,
    serverId: target === null ? null : serverIdOf(target),
    state: target === null ? GAME_STATE_CODES.LOGIN : GAME_STATE_CODES.CONNECTING,
    serverConfig: null,
    gameType: null,
    roomName: '',
    myID: null,
    myType: null,
    myTeam: null,
    myName: name,
    myScore: 0,
    myLevel: 0,
    spectatingID: null,
  };
}
